const DECIMAL_STRING_PATTERN = /^-?(?:0|[1-9]\d*)(?:\.\d+)?$/;
const CAPABILITY_KEYS = [
  'can_cancel',
  'can_void',
  'can_manual_settle',
  'can_restate',
  'can_approve_win',
  'can_override_selection',
];

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const hasText = (value) => typeof value === 'string' && value.trim() !== '';
const isNullableText = (value) => value === null || hasText(value);

export class SportsbookHistoryContractError extends Error {
  constructor(message) {
    super(message);
    this.name = 'SportsbookHistoryContractError';
  }
}

const assert = (condition, message) => {
  if (!condition) throw new SportsbookHistoryContractError(message);
};

export const isSportsbookHistoryDecimalString = (value) =>
  typeof value === 'string' && DECIMAL_STRING_PATTERN.test(value);

export const assertSportsbookHistoryRow = (row) => {
  assert(isObject(row), 'history row must be an object');
  for (const key of [
    'history_id',
    'bet_uid',
    'accepted_at',
    'status',
    'source_status',
    'status_label',
    'bet_type',
    'category',
    'category_label',
    'row_version',
    'archive_state',
  ]) {
    assert(hasText(row[key]), `history row ${key} must be a non-empty string`);
  }
  assert(isNullableText(row.settled_at), 'history row settled_at must be null or a non-empty string');
  assert(Number.isInteger(row.selection_count) && row.selection_count >= 0, 'history row selection_count must be a non-negative integer');
  for (const key of ['stake', 'display_total_odds', 'display_potential_win', 'display_actual_win']) {
    assert(isSportsbookHistoryDecimalString(row[key]), `history row ${key} must be a decimal string`);
  }
  assert(isObject(row.capabilities), 'history row capabilities must be an object');
  for (const key of CAPABILITY_KEYS) {
    assert(typeof row.capabilities[key] === 'boolean', `history capability ${key} must be boolean`);
  }
  return row;
};

const assertSelection = (selection) => {
  assert(isObject(selection), 'history selection must be an object');
  for (const key of [
    'selection_ref',
    'fixture_name',
    'market_name',
    'selection_name',
    'outcome',
    'outcome_label',
  ]) {
    assert(hasText(selection[key]), `history selection ${key} must be a non-empty string`);
  }
  assert(Number.isInteger(selection.selection_index) && selection.selection_index >= 0, 'history selection index must be a non-negative integer');
  assert(isSportsbookHistoryDecimalString(selection.accepted_odds), 'history selection accepted_odds must be a decimal string');
  for (const key of ['sport_name', 'league_name', 'fixture_start_at', 'line', 'score_at_bet', 'final_score', 'settled_at']) {
    assert(isNullableText(selection[key]), `history selection ${key} must be null or a non-empty string`);
  }
  assert(typeof selection.manual_override === 'boolean', 'history selection manual_override must be boolean');
  return selection;
};

export const assertSportsbookHistoryList = (payload) => {
  assert(isObject(payload), 'history list data must be an object');
  assert(Array.isArray(payload.rows), 'history list rows must be an array');
  assert(isObject(payload.page), 'history list page must be an object');
  assert(hasText(payload.page.snapshot_at), 'history page snapshot_at must be a non-empty string');
  assert(payload.page.next_cursor === null || hasText(payload.page.next_cursor), 'history page next_cursor must be null or a non-empty string');
  assert(typeof payload.page.has_more === 'boolean', 'history page has_more must be boolean');
  assert(!payload.page.has_more || hasText(payload.page.next_cursor), 'history page with more rows requires next_cursor');
  payload.rows.forEach((row) => {
    assertSportsbookHistoryRow(row);
    if (row.preview_selection !== null) assertSelection(row.preview_selection);
  });
  return payload;
};

export const assertSportsbookHistoryDetail = (payload) => {
  assertSportsbookHistoryRow(payload);
  assert(hasText(payload.detail_generation), 'history detail_generation must be a non-empty string');
  assert(hasText(payload.detail_hash), 'history detail_hash must be a non-empty string');
  assert(Array.isArray(payload.selections), 'history detail selections must be an array');
  payload.selections.forEach(assertSelection);
  return payload;
};
